import React, { useState } from 'react';
import { Pencil1Icon, TrashIcon } from '@radix-ui/react-icons';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { IconButton, TextInput } from '@producer/ui-kit';

import { DragIcon } from '../../../components/icons/DragIcon';
import { Property } from '../../../app/entities/property';

import styles from './sortable-option-item.module.scss';

interface SortableOptionItemProps {
  id: string;
  index: number;
  value: string;
  property: Property;
}

export const SortableOptionItem: React.FC<SortableOptionItemProps> = ({
  id,
  index,
  value,
  property,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleStartEdit = () => {
    setIsEditing(true);
    setEditValue(value);
  };

  const handleSaveEdit = () => {
    property.update({
      options: property.options.map((option, i) => (i === index ? editValue : option)),
    });
    setIsEditing(false);
  };

  const handleCancelEdit = () => {
    setEditValue(value);
    setIsEditing(false);
  };

  const handleDeleteOption = () => {
    property.update({
      options: property.options.filter((_, i) => i !== index),
    });
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSaveEdit();
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };

  return (
    <div ref={setNodeRef} style={style} className={styles.OptionItem}>
      <div className={styles.OptionDrag} {...attributes} {...listeners}>
        <DragIcon />
      </div>
      <div className={styles.OptionContent}>
        {isEditing ? (
          <TextInput
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            placeholder="Enter option text"
            onKeyDown={handleKeyDown}
            onBlur={handleSaveEdit}
            autoFocus
            inline
          />
        ) : (
          <p onClick={handleStartEdit}>{value || 'Empty option'}</p>
        )}
      </div>

      <div className={styles.OptionActions}>
        {!isEditing && <IconButton icon={<Pencil1Icon />} onClick={handleStartEdit}/>}

        <IconButton icon={<TrashIcon />} onClick={handleDeleteOption} />
      </div>
    </div>
  );
};
